import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck, Clock, Users, ListChecks, ChevronRight, Coffee,
  RefreshCw, Palmtree, Plus, DollarSign, UserCheck, Calendar,
  Megaphone, Gift, AlertTriangle, Zap, ClipboardList, Sun, Sunrise, Moon,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import DayPlannerModal from '../components/DayPlannerModal';

const API = process.env.REACT_APP_BACKEND_URL;
const TARGET = 480;

const fmtMin = (m) => {
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h === 0) return `${mins}m`;
  return mins ? `${h}h ${mins}m` : `${h}h`;
};

const fmtHours = (m) => {
  const h = Math.floor(m / 60);
  return `${h}h`;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const getGreetingIcon = () => {
  const hour = new Date().getHours();
  if (hour < 12) return <Sunrise className="h-5 w-5 text-amber-300" />;
  if (hour < 17) return <Sun className="h-5 w-5 text-amber-300" />;
  return <Moon className="h-5 w-5 text-indigo-300" />;
};

const formatDateShort = () =>
  new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const toTitleCase = (str) =>
  str ? str.toUpperCase() : '';

// ─── Priority badge colors ───
const priorityColor = (p) => {
  if (p === 'Urgent') return 'bg-red-100 text-red-700';
  if (p === 'High') return 'bg-blue-100 text-blue-700';
  if (p === 'Medium') return 'bg-amber-100 text-amber-700';
  return 'bg-zinc-100 text-zinc-600';
};

const statusColor = (s) => {
  if (s === 'Completed') return 'bg-emerald-600 text-white';
  if (s === 'In Progress') return 'bg-amber-500 text-white';
  if (s === 'On Hold') return 'bg-zinc-500 text-white';
  if (s === 'To Do') return 'bg-blue-500 text-white';
  return 'bg-zinc-400 text-white';
};

const statusDotColor = (s) => {
  if (s === 'Completed') return 'bg-blue-400';
  if (s === 'In Progress') return 'bg-amber-400';
  if (s === 'On Hold') return 'bg-zinc-400';
  return 'bg-blue-400';
};

// ─── Today at a Glance ───
const TodayAtAGlance = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReplan, setShowReplan] = useState(false);

  const firstName = toTitleCase(user?.full_name?.split(' ')[0] || user?.first_name || 'there');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/overview/dashboard`, { headers: getAuthHeaders() });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReplanComplete = () => {
    setShowReplan(false);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-80" data-testid="overview-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600" />
          <p className="text-sm text-zinc-500">Loading your day...</p>
        </div>
      </div>
    );
  }

  const plan = data?.day_plan;
  const tasks = data?.tasks || {};
  const meetings = data?.meetings || [];
  const birthdays = data?.birthdays || [];
  const holidays = data?.holidays || [];
  const announcements = data?.announcements || [];
  const totalPlanned = plan?.total_planned_minutes || 0;
  const pct = Math.min(100, Math.round((totalPlanned / TARGET) * 100));
  const hasBirthday = birthdays.length > 0;

  return (
    <div className="space-y-5" data-testid="today-at-a-glance">
      {showReplan && <DayPlannerModal forceReplan onComplete={handleReplanComplete} />}

      {/* ═══ HERO HEADER ═══ */}
      <div className="rounded-2xl bg-[#1e293b] text-white p-6 relative overflow-hidden" data-testid="overview-header">
        {/* Top label */}
        <p className="text-[11px] font-semibold tracking-widest text-zinc-400 uppercase mb-1">Today at a Glance</p>
        {/* Greeting */}
        <div className="flex items-center gap-2 mb-0.5">
          {getGreetingIcon()}
          <h1 className="text-xl font-bold" data-testid="overview-greeting">
            {getGreeting()}, {firstName}
          </h1>
        </div>
        <p className="text-sm text-zinc-400">{formatDateShort()}</p>

        {/* Summary badges row */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <span className="flex items-center gap-1.5 text-xs text-zinc-300">
            <ListChecks className="h-3.5 w-3.5" /> {tasks.total || 0} tasks
          </span>
          <span className="flex items-center gap-1.5 text-xs text-zinc-300">
            <Users className="h-3.5 w-3.5" /> {meetings.length} meetings
          </span>
          {(tasks.overdue || 0) > 0 && (
            <Badge className="bg-zinc-700 text-zinc-200 border-0 text-xs gap-1 px-2 py-0.5">
              <AlertTriangle className="h-3 w-3" /> {tasks.overdue} overdue
            </Badge>
          )}
          {hasBirthday && (
            <span className="flex items-center gap-1.5 text-xs text-zinc-300">
              <Gift className="h-3.5 w-3.5" /> Birthday!
            </span>
          )}
        </div>

        {/* Right section: Re-plan + progress circle */}
        <div className="absolute right-6 top-6 flex items-center gap-4">
          <button
            onClick={() => setShowReplan(true)}
            className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white transition-colors"
            data-testid="replan-day-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Re-plan
          </button>
          <div className="text-right mr-2 hidden sm:block">
            <p className="text-lg font-bold leading-tight">{fmtHours(totalPlanned)}</p>
            <p className="text-[10px] text-zinc-400 uppercase tracking-wider">of 8h planned</p>
          </div>
          {/* Circular progress */}
          <div className="relative h-12 w-12 hidden sm:flex items-center justify-center">
            <svg className="h-12 w-12 -rotate-90" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#374151" strokeWidth="3" />
              <circle
                cx="24" cy="24" r="20" fill="none"
                stroke={pct >= 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b'}
                strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${(pct / 100) * 125.6} 125.6`}
              />
            </svg>
            <span className="absolute text-xs font-bold">{pct}%</span>
          </div>
        </div>
      </div>

      {/* ═══ ROW 1: Planned / Meetings / Tasks ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Planned Card */}
        <Card className="shadow-sm" data-testid="planned-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-blue-50">
                <CalendarCheck className="h-4 w-4 text-blue-500" />
              </div>
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Planned</h3>
            </div>
            {plan && !plan.skipped ? (
              <div className="space-y-2">
                <p className="text-sm text-zinc-500">{fmtMin(totalPlanned)} planned for today</p>
                {plan.tasks?.length > 0 && (
                  <p className="text-xs text-zinc-400">{plan.tasks.length} task{plan.tasks.length !== 1 ? 's' : ''} selected</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic">Start planning your day</p>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-4 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => navigate('/timesheet')}
              data-testid="view-timesheet-btn"
            >
              View Timesheet <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>

        {/* Meetings Card */}
        <Card className="shadow-sm" data-testid="meetings-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-violet-50">
                <Users className="h-4 w-4 text-violet-500" />
              </div>
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Meetings</h3>
            </div>
            {meetings.length > 0 ? (
              <div className="space-y-2">
                {meetings.slice(0, 3).map((m, i) => (
                  <div key={m.id || i} className="text-sm text-zinc-700 dark:text-zinc-300">
                    <span className="font-medium">{m.title}</span>
                    {m.start_time && (
                      <span className="text-xs text-zinc-400 ml-2">{m.start_time} – {m.end_time}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic">No meetings today</p>
            )}
          </CardContent>
        </Card>

        {/* Tasks Card */}
        <Card className="shadow-sm" data-testid="tasks-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-emerald-50">
                  <ListChecks className="h-4 w-4 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Tasks</h3>
              </div>
              <span className="text-2xl font-bold text-zinc-800 dark:text-zinc-200">{tasks.total || 0}</span>
            </div>
            {(tasks.overdue || 0) > 0 && (
              <p className="text-xs text-blue-600 font-medium mb-2 flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                {tasks.overdue} overdue
              </p>
            )}
            <div className="space-y-1.5">
              {(tasks.top_active || []).slice(0, 2).map((t, i) => (
                <div key={t.id || i} className="flex items-center justify-between gap-2">
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 truncate flex-1">{t.title}</p>
                  <Badge className={`text-[10px] shrink-0 ${priorityColor(t.priority)}`}>{t.priority}</Badge>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-4 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => navigate('/my-tasks')}
              data-testid="view-tasks-btn"
            >
              View Tasks <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* ═══ ROW 2: Announcements / Holidays / Birthdays ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Announcements */}
        <Card className="shadow-sm" data-testid="announcements-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-indigo-50">
                <Megaphone className="h-4 w-4 text-indigo-500" />
              </div>
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Announcements</h3>
            </div>
            {announcements.length > 0 ? (
              <div className="space-y-2">
                {announcements.map((a, i) => (
                  <p key={a.id || i} className="text-sm text-zinc-700 dark:text-zinc-300 truncate">{a.title}</p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic">No announcements</p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Holidays */}
        <Card className="shadow-sm" data-testid="holidays-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-rose-50">
                <Palmtree className="h-4 w-4 text-rose-500" />
              </div>
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Upcoming Holidays</h3>
            </div>
            {holidays.length > 0 ? (
              <div className="space-y-2.5">
                {holidays.map((h, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-zinc-700 dark:text-zinc-300">{h.name}</span>
                    <span className="text-xs font-medium text-blue-600">
                      {new Date(h.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic">No upcoming holidays</p>
            )}
          </CardContent>
        </Card>

        {/* Birthdays Today */}
        <Card className="shadow-sm" data-testid="birthdays-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-purple-50">
                <Gift className="h-4 w-4 text-purple-500" />
              </div>
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Birthdays Today</h3>
            </div>
            {birthdays.length > 0 ? (
              <div className="space-y-3">
                {birthdays.map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                      {b.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{b.name}</span>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => navigate('/ping')}
                  data-testid="send-wish-btn"
                >
                  Send a wish <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic">No birthdays today</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ═══ ROW 3: Quick Links / My Tasks (Employee only) ═══ */}
      {!isAdmin && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Quick Links */}
        <Card className="shadow-sm" data-testid="quick-links-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-5">
              <Zap className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-200">Quick Links</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Request Leave', icon: Palmtree, color: 'text-green-600 bg-green-50', path: '/my-leave' },
                { label: 'Create Task', icon: Plus, color: 'text-emerald-600 bg-emerald-50', path: '/my-tasks' },
                { label: 'Attendance', icon: Clock, color: 'text-blue-600 bg-blue-50', path: '/attendance' },
                { label: 'My Payslips', icon: DollarSign, color: 'text-emerald-600 bg-emerald-50', path: '/my-salary' },
                { label: 'My Calendar', icon: Calendar, color: 'text-indigo-600 bg-indigo-50', path: '/my-calendar' },
                { label: 'My Profile', icon: UserCheck, color: 'text-pink-600 bg-pink-50', path: '/profile' },
              ].map((item) => (
                <Button
                  key={item.label}
                  variant="outline"
                  className="h-20 flex-col gap-2 hover:shadow-sm transition-all"
                  onClick={() => navigate(item.path)}
                  data-testid={`ql-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className={`p-2 rounded-lg ${item.color.split(' ')[1]}`}>
                    <item.icon className={`h-5 w-5 ${item.color.split(' ')[0]}`} />
                  </div>
                  <span className="text-xs font-medium">{item.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* My Tasks */}
        <Card className="shadow-sm" data-testid="my-tasks-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-blue-500" />
                <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-200">My Tasks</h3>
              </div>
              <button
                onClick={() => navigate('/my-tasks')}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="view-all-tasks-btn"
              >
                View All <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {(tasks.recent || []).length > 0 ? (
              <div className="space-y-3">
                {(tasks.recent || []).slice(0, 5).map((t, i) => (
                  <div
                    key={t.id || i}
                    className="flex items-center gap-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50 rounded-lg p-2 -mx-2 transition-colors"
                    onClick={() => navigate('/my-tasks')}
                    data-testid={`my-task-${i}`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotColor(t.status)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-800 dark:text-zinc-200 truncate">{t.title}</p>
                      {t.due_date && (
                        <p className="text-xs text-zinc-400">
                          Due: {new Date(t.due_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <Badge className={`text-[10px] shrink-0 border-0 ${statusColor(t.status)}`}>
                      {t.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic text-center py-8">No tasks assigned</p>
            )}
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
};

// Office Admin Dashboard - Today at a Glance + Office Analytics
const OfficeAdminDashboard = () => {
  const AnalyticsDashboard = React.lazy(() => import('./AnalyticsDashboard'));
  return (
    <div className="space-y-6">
      <TodayAtAGlance isAdmin />
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center h-48">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600" />
              <p className="text-sm text-zinc-500">Loading analytics...</p>
            </div>
          </div>
        }
      >
        <AnalyticsDashboard />
      </React.Suspense>
    </div>
  );
};

// Super Admin Dashboard (unchanged)
const SuperAdminDashboard = () => {
  const SuperAdminAnalyticsDashboard = React.lazy(() => import('./SuperAdminAnalyticsDashboard'));
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600" />
            <p className="text-gray-500">Loading analytics...</p>
          </div>
        </div>
      }
    >
      <SuperAdminAnalyticsDashboard />
    </React.Suspense>
  );
};

// Main Overview Component
const Overview = () => {
  const { isSuperAdmin, isViewingAsAdmin, isEmployee } = useAuth();

  if (isSuperAdmin()) {
    return <SuperAdminDashboard />;
  }

  if (isViewingAsAdmin()) {
    return <OfficeAdminDashboard />;
  }

  // Employee view (regular employee OR office admin in employee view)
  return <TodayAtAGlance />;
};

export default Overview;
